import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { NewReportModalComponent } from './components/new-report-modal/new-report-modal.component';
import { ReportAutomationService } from './services/report-automation.service';
import { Report, ReportRequestRow, ReportStatus, ReportTypeDto } from './shared/interfaces';
import { STORAGE_KEY } from '../../definitions/app.constants';

const POLL_INTERVAL_MS = 60000;

interface ReportRow extends Report {
  statusBg: string;
  statusFg: string;
}

@Component({
  selector: 'app-report-automation',
  templateUrl: './report-automation.component.html',
  styleUrls: ['./report-automation.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportAutomationComponent implements OnInit, OnDestroy {
  reports: Report[] = [];
  totalItems = 0;
  loading = false;
  search = '';
  page = 1;
  pageSize = 5;

  filterType = '';
  filterFrom = '';
  filterTo = '';

  reportTypes: ReportTypeDto[] = [];
  get reportTypeOptions(): string[] {
    return ['All types', ...this.reportTypes.map(t => t.name)];
  }

  private _userEmail = '';
  private _pollTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly SUBSCRIPTION = new Subscription();

  constructor(
    private readonly _dialog: MatDialog,
    private readonly _raService: ReportAutomationService
  ) {}

  ngOnInit(): void {
    this._userEmail = localStorage.getItem(STORAGE_KEY.email) || '';
    this.SUBSCRIPTION.add(
      this._raService.getReportTypes().subscribe(types => {
        this.reportTypes = types;
      })
    );
    this._loadReports();
  }

  ngOnDestroy(): void {
    this.SUBSCRIPTION.unsubscribe();
    this._clearPoll();
  }

  get filtered(): Report[] {
    const q = this.search.trim().toLowerCase();
    return this.reports.filter(r => {
      const matchesSearch = !q || (
        r.type.toLowerCase().includes(q) ||
        r.maker.toLowerCase().includes(q) ||
        r.country.toLowerCase().includes(q)
      );
      const matchesType = !this.filterType || r.type === this.filterType;
      const matchesFrom = !this.filterFrom || r.date >= this.filterFrom;
      const matchesTo   = !this.filterTo   || r.date <= this.filterTo;
      return matchesSearch && matchesType && matchesFrom && matchesTo;
    });
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }

  get currentPage(): number {
    return Math.min(this.page, this.pageCount);
  }

  get pageItems(): ReportRow[] {
    return this.filtered.map(r => ({ ...r, ...this._statusColors(r.status) }));
  }

  trackById(_: number, r: Report): string {
 return r.id;
}

  get rangeLabel(): string {
    if (this.totalItems === 0) {
 return '0 of 0 items';
}
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalItems);
    return `${start} – ${end} of ${this.totalItems} items`;
  }

  get canPrev(): boolean {
 return this.currentPage > 1;
}
  get canNext(): boolean {
 return this.currentPage < this.pageCount;
}
  get noResults(): boolean {
 return this.totalItems === 0;
}
  get hasActiveFilters(): boolean {
 return !!(this.filterType || this.filterFrom || this.filterTo);
}

  onSearch(value: string): void {
    this.search = value;
    this.page = 1;
    this._loadReports();
  }

  onPageSize(value: number): void {
    this.pageSize = value;
    this.page = 1;
    this._loadReports();
  }

  onFilterType(value: string): void {
    this.filterType = value === 'All types' ? '' : value;
    this.page = 1;
    this._loadReports();
  }

  get filterTypeDisplay(): string {
    return this.filterType || 'All types';
  }

  onFilterFrom(date: string): void {
    this.filterFrom = date;
    this.page = 1;
    this._loadReports();
  }

  onFilterTo(date: string): void {
    this.filterTo = date;
    this.page = 1;
    this._loadReports();
  }

  resetFilters(): void {
    this.filterType = '';
    this.filterFrom = '';
    this.filterTo = '';
    this.page = 1;
    this._loadReports();
  }

  prevPage(): void {
    if (this.canPrev) {
      this.page = this.currentPage - 1;
      this._loadReports();
    }
  }

  nextPage(): void {
    if (this.canNext) {
      this.page = this.currentPage + 1;
      this._loadReports();
    }
  }

  openNewReport(): void {
    const ref = this._dialog.open(NewReportModalComponent, {
      panelClass: 'ra-dialog-panel',
      data: null
    });
    this.SUBSCRIPTION.add(
      ref.afterClosed().subscribe(result => {
        if (!result) {
 return;
}
        this._loadReports();
      })
    );
  }

  private _loadReports(): void {
    if (!this._userEmail) {
 return;
}
    this.loading = true;
    this.SUBSCRIPTION.add(
      this._raService.getReportRequests(this._userEmail, this.page, this.pageSize)
        .subscribe(res => {
          this.totalItems = res.total_rows;
          this.reports = res.rows.map(row => this._mapRow(row));
          this.loading = false;
          const hasInProgress = this.reports.some(r => r.status === 'In progress');
          if (hasInProgress) {
            this._schedulePoll();
          } else {
            this._clearPoll();
          }
        })
    );
  }

  private _schedulePoll(): void {
    this._clearPoll();
    this._pollTimer = setTimeout(() => {
 this._loadReports();
}, POLL_INTERVAL_MS);
  }

  private _clearPoll(): void {
    if (this._pollTimer !== null) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private _mapRow(row: ReportRequestRow): Report {
    const dt = new Date(row.created_at);
    const date = row.created_at.split('T')[0];
    const dateLabel = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timeLabel = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
    const requestedAt = `${dateLabel} · ${timeLabel}`;
    return {
      id: row.id,
      type: row.report_type_name,
      maker: row.maker_name,
      country: row.country_name,
      status: this._mapStatus(row.status),
      url: row.report_url,
      date,
      requestedAt
    };
  }

  private _mapStatus(apiStatus: string): ReportStatus {
    if (apiStatus === 'CREATED') {
 return 'Ready';
}
    if (apiStatus === 'FAILED') {
 return 'Failed';
}
    return 'In progress';
  }

  private _statusColors(status: ReportStatus): { statusBg: string; statusFg: string } {
    if (status === 'Ready') {
 return { statusBg: '#dcf3e5', statusFg: '#178b4e' };
}
    if (status === 'In progress') {
 return { statusBg: '#fbefcb', statusFg: '#946a00' };
}
    return { statusBg: '#fce4e4', statusFg: '#e5443b' };
  }

}
